import React, { useState, memo, useEffect, useCallback, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  TouchableOpacity,
  ScrollView,
} from "react-native";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { LinearGradient } from "expo-linear-gradient";

import { AppFontStyle } from "../../../../styles/AppFontStyle";
import { AppColors } from "../../../../utility/AppColors";
import ChatPanel from "../../../../Components/stream-setup-components/ChatPanel";
import { toast } from "react-toastify";
import { getNotificationFromEvent } from "../../../../services/eventsAPI";
import { useNotifications } from "../../../../context/NotificationContext";
import { base_url, endpoints } from "../../../../Api/configs";
import CornfirmPurchaseBeltModal from "../BeltScreens/CornfirmPurchaseBeltModal";
import BeltPurchaseSuccessModal from "../BeltScreens/BeltPurchaseSuccessModal";
import { useNavigation } from "@react-navigation/native";
import { navigateToScreen } from "../../../../utility/NavigationHelper";
import { AppScreens } from "../../../Utility/AppScreens";
import { storeUserActivity, ACTIVITY_TYPES } from "../../../../services/activityService";

interface OnGoingGameShowChatProps {
  ChatConversationId: string;
  connectionReady?: boolean;
  isLoading?: boolean;
  eventID?: string;
  accessToken?: string;
  userData?: any;
  isOrganiserId?: boolean;
  details?: any;
  refreshEvent?: () => void;
  IsEventCanceled?: boolean;
  IsEventCompleted?: boolean;
  arrBeltMedalType?: any[];
  setArrBeltMedalType?: (belts: any[]) => void;
  userBalanceData?: any;
}

type ExpandedChat = "event" | "participants" | null;
type ChatTab = "event" | "participants";

const OnGoingGameShowChat: React.FC<OnGoingGameShowChatProps> = memo(
  ({
    ChatConversationId,
    connectionReady = true,
    isLoading = false,
    eventID,
    accessToken,
    userData,
    isOrganiserId = false,
    details,
    refreshEvent,
    IsEventCanceled = false,
    IsEventCompleted = false,
    arrBeltMedalType = [],
    setArrBeltMedalType,
    userBalanceData,
  }) => {
    const styles = useStyles();
    const deviceSize = useDeviceSize();
    const navigation = useNavigation();
    const [expandedChat, setExpandedChat] = useState<ExpandedChat>(null);
    const [selectedChatTab, setSelectedChatTab] = useState<ChatTab>("event");
    const [notificationData, setNotificationData] = useState<any>(null);
    const [showAPILoader, setAPILoader] = useState(false);
    const isMountedRef = useRef(true);
    const [cornfirmPurchaseBeltModal, setCornfirmPurchaseBeltModal] = useState(false);
    const [beltPurchaseSuccessModal, setBeltPurchaseSuccessModal] = useState(false);
    const [insufficientStakesError, setInsufficientStakesError] = useState<any>(null);
    const [numberOfBelts, setNumberOfBelts] = useState("0");

    const {
      eventStatus,
      updateNotification,
      sendNotification,
      setUpdateNotification,
    } = useNotifications();

    const isSmallScreen =
      deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    // Fetch notification data
    const getNotificationFromEventAPI = useCallback(async () => {
      if (!accessToken || !eventID || !isMountedRef.current) {
        return;
      }

      try {
        const response = await getNotificationFromEvent(accessToken, eventID);

        if (!isMountedRef.current) return;

        if (response?.statusCode === 200) {
          if (response.body?.notification) {
            setNotificationData(response.body.notification);
          }
        }
      } catch (error: any) {
        if (isMountedRef.current) {
          console.error("Failed to fetch notification data:", error);
        }
      }
    }, [accessToken, eventID]);

    // Load notification data on mount
    useEffect(() => {
      if (accessToken && eventID) {
        getNotificationFromEventAPI();
      }
    }, [getNotificationFromEventAPI]);

    // Listen for challenge status updates
    useEffect(() => {
      if (eventStatus && eventStatus.eventId === eventID) {
        console.log(
          "=== OnGoingGameShowChat: Challenge status update received ==="
        );
        if (refreshEvent) refreshEvent();
        getNotificationFromEventAPI();
      }
    }, [eventStatus, eventID, refreshEvent, getNotificationFromEventAPI]);

    // Listen for opponent changes
    useEffect(() => {
      if (
        updateNotification &&
        typeof updateNotification === "object" &&
        "challengeId" in updateNotification &&
        updateNotification.challengeId === eventID
      ) {
        console.log(
          "=== OnGoingGameShowChat: Opponent change notification received ==="
        );
        if (refreshEvent) refreshEvent();
        getNotificationFromEventAPI();
      }
    }, [
      updateNotification,
      eventID,
      refreshEvent,
      getNotificationFromEventAPI,
    ]);

    // Handle accept/reject challenge
    const handleChallengeResponse = useCallback(
      async (userResponse: boolean) => {
        if (!accessToken || !notificationData?.notificationId) {
          toast.error("Missing required information to respond to challenge");
          return;
        }

        setAPILoader(true);
        const url = `${base_url}${endpoints.events.challengeRespond}`;

        try {
          const response = await fetch(url, {
            mode: "cors",
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              accessToken,
              notificationId: notificationData.notificationId,
              response: userResponse,
            }),
          });

          const data = await response.json();

          if (response.status === 200) {
            if (data.statusCode === 200 || data.statusCode === 201) {
              toast.success(
                userResponse
                  ? "Challenge Accepted Successfully"
                  : "Challenge Rejected Successfully"
              );

              const { body } = data;
              const { notificationId, senderId } = body;

              const updateResponse = {
                challengeId: eventID,
                response: userResponse,
              };

              setUpdateNotification(updateResponse);
              await sendNotification(senderId, notificationId);

              // Track activity (fire and forget)
              storeUserActivity(
                accessToken,
                eventID,
                userResponse ? ACTIVITY_TYPES.CHALLENGE_ACCEPTED : ACTIVITY_TYPES.CHALLENGE_REJECTED
              ).catch((err) => console.warn("Failed to track activity:", err));

              // Refresh event details and notification data
              if (refreshEvent) refreshEvent();
              getNotificationFromEventAPI();
            } else if (data.statusCode === 400) {
              // Handle insufficient stakes error by showing belt purchase modal
              let errorMessage = "";
              if (data.body && typeof data.body === "object" && data.body.error) {
                errorMessage = data.body.error;
              } else if (typeof data.body === "string") {
                errorMessage = data.body;
              }

              // Extract required belts from error message
              const match = errorMessage.match(/Required:\s*(\d+)\s*(\w+)/i);
              if (match) {
                const requiredAmount = parseInt(match[1]);
                const beltType = match[2];

                setInsufficientStakesError({
                  requiredAmount,
                  beltType,
                  fullMessage: errorMessage,
                });

                // Calculate how many belts the user needs to buy
                const userBronzeBelts = arrBeltMedalType.find((b) => b.title === "Bronze")?.value || 0;
                const beltsToBuy = Math.max(0, requiredAmount - userBronzeBelts);
                setNumberOfBelts(beltsToBuy.toString());

                setCornfirmPurchaseBeltModal(true);
              } else {
                // If we can't parse the error, show it as toast
                toast.error(errorMessage || "Insufficient belts to accept this challenge");
              }
            } else {
              try {
                const parsedData = JSON.parse(data.body);
                toast.error(parsedData.message || "An error occurred");
              } catch (error) {
                toast.error(data.body);
              }
            }
          } else {
            toast.error("Failed to respond to challenge");
          }
        } catch (error: any) {
          if (isMountedRef.current) {
            toast.error(error.message || "Failed to respond to challenge");
          }
        } finally {
          if (isMountedRef.current) {
            setAPILoader(false);
          }
        }
      },
      [
        accessToken,
        notificationData,
        eventID,
        refreshEvent,
        getNotificationFromEventAPI,
        sendNotification,
        setUpdateNotification,
      ]
    );

    const handleEventChatExpand = (expand: boolean) => {
      setExpandedChat(expand ? "event" : null);
    };

    const handleParticipantChatExpand = (expand: boolean) => {
      setExpandedChat(expand ? "participants" : null);
    };

    // Check if we should show accept/reject buttons
    const showChallengeButtons =
      !isOrganiserId &&
      notificationData?.actionStatus === "pending" &&
      details?.participantDetails?.[1]?.userID === userData?.username &&
      !IsEventCanceled &&
      !IsEventCompleted;

    // Check if chat should be locked (challenge not accepted yet)
    const isChatLocked =
      !details?.ChatConversationId ||
      (notificationData?.actionStatus !== "accepted" &&
        notificationData?.actionStatus !== null &&
        notificationData?.actionStatus !== undefined);

    return (
      <ScrollView
        style={styles.scrollContainer}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.container}>
          {/* Mobile Tabs */}
          {isSmallScreen && (
            <LinearGradient
              colors={["#1F2937", "#111827"]}
              start={[0, 0]}
              end={[0, 1]}
              style={styles.tabsContainer}
            >
              <TouchableOpacity
                onPress={() => setSelectedChatTab("event")}
                style={[
                  styles.tab,
                  selectedChatTab === "event" && styles.selectedTab,
                ]}
              >
                <LinearGradient
                  colors={
                    selectedChatTab === "event"
                      ? ["#0C1119", "#0C1119"]
                      : ["transparent", "transparent"]
                  }
                  start={[0, 0]}
                  end={[0, 1]}
                  style={[
                    styles.tabGradient,
                    selectedChatTab === "event" && styles.selectedTabGradient,
                  ]}
                >
                  <Text
                    style={[
                      AppFontStyle.BOLD_ANYBODY_14,
                      {
                        color: "#FFFFFF",
                        fontWeight: "700",
                      },
                    ]}
                  >
                    Live event chat
                  </Text>
                </LinearGradient>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => setSelectedChatTab("participants")}
                style={[
                  styles.tab,
                  selectedChatTab === "participants" && styles.selectedTab,
                ]}
              >
                <LinearGradient
                  colors={
                    selectedChatTab === "participants"
                      ? ["#0C1119", "#0C1119"]
                      : ["transparent", "transparent"]
                  }
                  start={[0, 0]}
                  end={[0, 1]}
                  style={[
                    styles.tabGradient,
                    selectedChatTab === "participants" &&
                    styles.selectedTabGradient,
                  ]}
                >
                  <Text
                    style={[
                      AppFontStyle.BOLD_ANYBODY_14,
                      {
                        color: "#FFFFFF",
                        fontWeight: "700",
                      },
                    ]}
                  >
                    Participant chat
                  </Text>
                </LinearGradient>
              </TouchableOpacity>
            </LinearGradient>
          )}

          {/* Challenge Accept/Reject Section */}
          {showChallengeButtons && (
            <View style={styles.challengeButtonsContainer}>
              <View style={styles.challengeMessageContainer}>
                <Text
                  style={[
                    AppFontStyle.BOLD_ANYBODY_14,
                    { color: AppColors.white, textAlign: "center" },
                  ]}
                >
                  You have a pending challenge from{" "}
                  {details?.participantDetails?.[0]?.userID}
                </Text>
              </View>
              <View style={styles.buttonsRow}>
                {/* Reject Button */}
                <TouchableOpacity
                  onPress={() => handleChallengeResponse(false)}
                  disabled={showAPILoader}
                  style={[
                    styles.rejectButton,
                    { opacity: showAPILoader ? 0.5 : 1 },
                  ]}
                >
                  <Text
                    style={[
                      AppFontStyle.BOLD_ANYBODY_12,
                      {
                        color: AppColors.buttonRed_DC2626,
                        fontWeight: "700",
                        textAlign: "center",
                      },
                    ]}
                  >
                    {showAPILoader ? "Processing..." : "Reject"}
                  </Text>
                </TouchableOpacity>

                {/* Accept Button */}
                <TouchableOpacity
                  onPress={() => handleChallengeResponse(true)}
                  disabled={showAPILoader}
                  style={[
                    styles.acceptButton,
                    { opacity: showAPILoader ? 0.5 : 1 },
                  ]}
                >
                  <Text
                    style={[
                      AppFontStyle.BOLD_ANYBODY_12,
                      {
                        color: AppColors.white,
                        fontWeight: "700",
                        textAlign: "center",
                      },
                    ]}
                  >
                    {showAPILoader ? "Processing..." : "Accept"}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          )}

          {!ChatConversationId ? (
            <View style={styles.emptyStateContainer}>
              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_16,
                  { color: AppColors.white, textAlign: "center" },
                ]}
              >
                Chat will be available once the challenge is accepted
              </Text>
            </View>
          ) : isLoading || !connectionReady ? (
            <View style={styles.emptyStateContainer}>
              <ActivityIndicator
                size="large"
                color={AppColors.appViolentColor_6D08C3}
              />
              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_14,
                  {
                    color: AppColors.white,
                    textAlign: "center",
                    marginTop: 10,
                  },
                ]}
              >
                Connecting to chat...
              </Text>
            </View>
          ) : (
            <View style={styles.chatPanelsContainer}>
              {/* Desktop: Show both chats side by side */}
              {!isSmallScreen && (
                <>
                  <View
                    style={[
                      styles.chatPanelDesktop,
                      expandedChat === "event" ? styles.fullScreenChat : {},
                    ]}
                  >
                    <ChatPanel
                      variant="event"
                      setIsExpandChat={handleEventChatExpand}
                      isExpandChat={expandedChat === "event"}
                      isChatLocked={isChatLocked}
                    />
                  </View>

                  <View
                    style={[
                      styles.chatPanelDesktop,
                      expandedChat === "participants" ? styles.fullScreenChat : {},
                    ]}
                  >
                    <ChatPanel
                      variant="participants"
                      setIsExpandChat={handleParticipantChatExpand}
                      isExpandChat={expandedChat === "participants"}
                    />
                  </View>
                </>
              )}

              {/* Mobile: Show selected tab only */}
              {isSmallScreen && (
                <View style={styles.chatPanelMobile}>
                  {selectedChatTab === "event" ? (
                    <ChatPanel
                      variant="event"
                      setIsExpandChat={handleEventChatExpand}
                      isExpandChat={expandedChat === "event"}
                      isChatLocked={isChatLocked}
                    />
                  ) : (
                    <ChatPanel
                      variant="participants"
                      setIsExpandChat={handleParticipantChatExpand}
                      isExpandChat={expandedChat === "participants"}
                    />
                  )}
                </View>
              )}
            </View>
          )}

          {/* Belt Purchase Confirmation Modal */}
          {cornfirmPurchaseBeltModal && (
            <CornfirmPurchaseBeltModal
              accessToken={accessToken}
              numberOfBelts={numberOfBelts}
              setArrBeltMedalType={setArrBeltMedalType}
              arrBeltMedalType={arrBeltMedalType}
              showModal={cornfirmPurchaseBeltModal}
              insufficientStakesError={insufficientStakesError}
              setShowModal={setCornfirmPurchaseBeltModal}
              eventID={eventID}
              selectedBelt={() => {
                setBeltPurchaseSuccessModal(true);
              }}
              userBalanceData={userBalanceData}
              jumpToPurchaseToken={(isNavigate) => {
                if (isNavigate) {
                  // Store the state that we need to return to belt modal
                  localStorage.setItem('returnToBeltModal', JSON.stringify({
                    eventID,
                    insufficientStakesError,
                    numberOfBelts,
                  }));
                  navigateToScreen(navigation, AppScreens.PurchaseScreen, {
                    eventID,
                    type: "ongoing",
                  });
                }
              }}
              setRefreshAPICall={() => {
                if (refreshEvent) refreshEvent();
              }}
            />
          )}

          {/* Belt Purchase Success Modal */}
          {beltPurchaseSuccessModal && (
            <BeltPurchaseSuccessModal
              showModal={beltPurchaseSuccessModal}
              setShowModal={setBeltPurchaseSuccessModal}
              arrBeltMedalType={arrBeltMedalType}
              numberOfBelts={numberOfBelts}
              backProfileTapped={() => {
                setBeltPurchaseSuccessModal(false);
                if (refreshEvent) refreshEvent();
              }}
              dataBuyBelt={insufficientStakesError}
            />
          )}
        </View>
      </ScrollView>
    );
  }
);

OnGoingGameShowChat.displayName = "OnGoingGameShowChat";

const useStyles = CreateResponsiveStyle(
  {
    scrollContainer: {
      flex: 1,
    },
    scrollContent: {
      flexGrow: 1,
    },
    container: {
      borderColor: AppColors.gray_1E1E1E,
      borderWidth: 1,
      borderRadius: 12,
      marginVertical: 15,
    },
    header: {
      marginHorizontal: 20,
      marginVertical: 10,
      flexDirection: "row",
      gap: 5,
      justifyContent: "center",
      alignItems: "center",
    },
    tabsContainer: {
      flexDirection: "row",
      padding: 8,
      gap: 18,
      borderRadius: 8,
    },
    tab: {
      flex: 1,
    },
    tabGradient: {
      height: 41,
      paddingVertical: 12,
      justifyContent: "center",
      alignItems: "center",
      borderRadius: 8,
    },
    selectedTab: {
      // Additional styles for selected tab if needed
    },
    selectedTabGradient: {
      borderWidth: 0.8,
      borderColor: "#374151",
    },
    emptyStateContainer: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      padding: 20,
    },
    chatPanelsContainer: {
      flexDirection: "row",
      gap: 20,
      padding: 20,
    },
    chatPanelDesktop: {
      flex: 1,
      minWidth: "45%",
    },
    chatPanelMobile: {
      width: "100%",
      marginBottom: 20,
    },
    fullScreenChat: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: "rgba(0, 0, 0, 0.9)",
      zIndex: 999,
      padding: 10,
    },
    challengeButtonsContainer: {
      backgroundColor: "rgba(220, 38, 38, 0.1)",
      borderColor: AppColors.buttonRed_DC2626,
      borderWidth: 1,
      borderRadius: 8,
      padding: 15,
      marginHorizontal: 20,
      marginBottom: 15,
      alignSelf: "center",
      maxWidth: 600,
    },
    challengeMessageContainer: {
      marginBottom: 12,
      alignItems: "center",
    },
    buttonsRow: {
      flexDirection: "row",
      gap: 10,
      justifyContent: "center",
      flexWrap: "wrap",
    },
    rejectButton: {
      paddingVertical: 10,
      paddingHorizontal: 25,
      backgroundColor: "transparent",
      borderWidth: 2,
      borderColor: AppColors.red_ED1F24,
      borderRadius: 8,
      minWidth: 100,
    },
    acceptButton: {
      paddingVertical: 10,
      paddingHorizontal: 25,
      backgroundColor: AppColors.appGreenColor_51AF07,
      borderRadius: 8,
      minWidth: 100,
    },
  },
  {
    [DEVICE_SIZES.XS]: {
      container: {
        width: "100%",
      },
      chatPanelsContainer: {
        flexDirection: "column",
        gap: 10,
        padding: 10,
      },
      header: {
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 10,
      },
      challengeButtonsContainer: {
        marginHorizontal: 10,
        padding: 12,
        maxWidth: "100%",
      },
      buttonsRow: {
        flexDirection: "column",
        gap: 8,
      },
      rejectButton: {
        width: "100%",
      },
      acceptButton: {
        width: "100%",
      },
    },
    [DEVICE_SIZES.SM]: {
      container: {
        width: "100%",
      },
      chatPanelsContainer: {
        flexDirection: "column",
        gap: 10,
        padding: 10,
      },
      header: {
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 10,
      },
      challengeButtonsContainer: {
        marginHorizontal: 15,
        maxWidth: "90%",
      },
    },
    [DEVICE_SIZES.MD]: {
      container: {
        width: "100%",
      },
      chatPanelsContainer: {
        flexDirection: "column",
        gap: 15,
        padding: 15,
      },
      challengeButtonsContainer: {
        maxWidth: 500,
      },
    },
  }
);

export default OnGoingGameShowChat;
